import { createRazorpayPricingOrder, verifyRazorpayPricingPayment } from '../api';
import { loadRazorpayCheckout } from './loadRazorpay';

export const PENDING_PRICING_CHECKOUT_KEY = 'pendingPricingCheckout';

export function setPendingPricingCheckout(planId, billingCycle) {
  try {
    localStorage.setItem(
      PENDING_PRICING_CHECKOUT_KEY,
      JSON.stringify({ planId, billingCycle })
    );
  } catch (_) {}
}

export function consumePendingPricingCheckout() {
  try {
    const raw = localStorage.getItem(PENDING_PRICING_CHECKOUT_KEY);
    if (!raw) return null;
    localStorage.removeItem(PENDING_PRICING_CHECKOUT_KEY);
    return JSON.parse(raw);
  } catch {
    try {
      localStorage.removeItem(PENDING_PRICING_CHECKOUT_KEY);
    } catch (_) {}
    return null;
  }
}

/**
 * Opens Razorpay checkout for a pricing plan (requires authenticated API).
 * @param {object} params
 * @param {object} params.plan - entry from PRICING_PLANS
 * @param {'monthly'|'yearly'} params.billingCycle
 * @param {string} params.token - JWT
 * @param {(msg: string, type?: string) => void} [params.onNotify]
 * @param {() => void} [params.onFinally] - always called when flow stops (error, dismiss, or after handler)
 */
export async function openRazorpayPricingCheckout({
  plan,
  billingCycle,
  token,
  couponCode,
  onNotify,
  onSuccess,
  onFinally,
}) {
  const notify = (message, type = 'info') => {
    if (onNotify) onNotify(message, type);
    else if (type === 'error') console.error(message);
    else console.log(message);
  };

  const done = () => {
    try {
      onFinally?.();
    } catch (_) {}
  };

  try {
    const loaded = await loadRazorpayCheckout();
    if (!loaded) {
      notify('Unable to load payment gateway. Please try again.', 'error');
      done();
      return;
    }

    const orderRes = await createRazorpayPricingOrder(plan.id, billingCycle, token, couponCode);
    const payload = orderRes?.data?.data;
    const key = payload?.razorpayKeyId;
    const order = payload?.order;
    const pricing = payload?.pricing;

    if (!key || !order?.id) {
      notify('Failed to start payment. Please try again.', 'error');
      done();
      return;
    }

    const planLabel = pricing?.planName || plan.name;
    const prefill = {
      name: localStorage.getItem('name') || undefined,
      email: localStorage.getItem('email') || undefined,
      contact: localStorage.getItem('phone') || undefined,
    };

    const options = {
      key,
      amount: order.amount,
      currency: order.currency,
      name: 'Fashdeck',
      description: `${planLabel} — ${plan.periodLabel || billingCycle}`,
      order_id: order.id,
      prefill,
      notes: order.notes || undefined,
      theme: { color: '#5424e4' },
      modal: {
        ondismiss: () => done(),
      },
      handler: async (response) => {
        try {
          const verifyRes = await verifyRazorpayPricingPayment(
            {
              planId: plan.id,
              billingCycle,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              ...(couponCode ? { couponCode } : {}),
            },
            token
          );

          try {
            const detail = verifyRes?.data?.data || null;
            window.dispatchEvent(new CustomEvent('fashdeck:credits-updated', { detail }));
          } catch (_) {}

          try {
            onSuccess?.(verifyRes);
          } catch (_) {}

          notify(`Payment successful. Thank you for choosing ${planLabel}!`, 'success');
        } catch (err) {
          console.error('Pricing payment verification error:', err);
          notify(
            err?.response?.data?.message || 'Payment verification failed. Please contact support.',
            'error'
          );
        } finally {
          done();
        }
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', (resp) => {
      const msg =
        resp?.error?.description ||
        resp?.error?.reason ||
        resp?.error?.code ||
        'Payment failed. Please try again.';
      notify(msg, 'error');
      done();
    });
    rzp.open();
  } catch (error) {
    console.error('Pricing purchase error:', error);
    notify(error.response?.data?.message || 'Could not start payment. Please try again.', 'error');
    done();
  }
}
